import { useState, useEffect, useRef } from 'react';
import { Button } from './ui.jsx';

/**
 * A tracking link and a button that copies it.
 *
 * Renders as a fragment so the caller's flex row decides the layout; a link
 * shown in a partnership card and one shown inline after applying sit in
 * different containers.
 *
 * `onError` receives the message when the browser refuses clipboard access
 * (insecure context, denied permission), so a page can show it in its own
 * alert; without one it is shown beside the button. Either way the link stays
 * selectable, so the person is never left without a way to get it.
 *
 * Also used for creator codes, with `noun="code"` so the fallback message reads right.
 */
export function TrackingLink({ url, onError, noun = 'link' }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef(null);

  // A pending reset must not fire into an unmounted card.
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setFailed(false);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      const message = `Could not copy — select the ${noun} and copy it manually.`;
      if (onError) onError(message);
      else setFailed(true);
    }
  }

  return (
    <>
      <code className="rounded bg-paper-sunken px-2 py-1 text-xs">{url}</code>
      <Button size="sm" variant="outline" onClick={copy}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
      {failed && (
        <span className="text-xs text-negative" role="alert">
          Could not copy — select the {noun} and copy it manually.
        </span>
      )}
    </>
  );
}

export default TrackingLink;
